import { useRef, useState } from 'react';
import { MoreVert } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { Dialog } from 'components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useForgotPassword, useResendActivation } from 'features/auth/hooks/use-auth';
import { UserDetails } from 'features/iam';
import { useDeactivateUser } from 'features/iam/hooks/use-iam';
import type { IamData } from 'features/iam/services/user-service';
import { EditIamProfileDetails } from 'features/profile/component/modals/edit-iam-profile-details/edit-iam-profile-details';
import { useCommonStore } from 'state/store/common/useCommonStore';

type TeamsMemberActionsMenuProps = {
  user: IamData;
  memberName: string;
  triggerClassName?: string;
};

type OpenDialog = 'details' | 'edit' | 'resetPassword' | 'resendActivation' | 'deactivate' | null;

// Hidden for now; set to true to show "View usage" in the row menu again.
const SHOW_VIEW_USAGE = false;

const itemClassName =
  'h-10 cursor-pointer rounded-field px-2.5 py-0 text-sm font-normal leading-body focus:bg-grey-50 dark:focus:bg-muted';

/** People row more menu — same actions as the Identity Management row menu. */
export function TeamsMemberActionsMenu({
  user,
  memberName,
  triggerClassName,
}: Readonly<TeamsMemberActionsMenuProps>) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const currentUserId = useCommonStore((state) => state.user?.id);
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  // The details drawer is non-modal and closes as soon as focus leaves it. While the menu fades out
  // its items can still take focus (and closing it focuses the ⋮ button), so the drawer opens only
  // once the menu is gone, in onCloseAutoFocus.
  const openDetailsAfterClose = useRef(false);
  const { mutateAsync: resetPassword } = useForgotPassword();
  const { mutateAsync: resendActivation } = useResendActivation();
  const { mutateAsync: deactivateUser } = useDeactivateUser();
  const mutedColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-500';
  const itemColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const isCurrentUser = currentUserId === user.itemId;

  const closeDialog = (open: boolean) => {
    if (!open) setOpenDialog(null);
  };

  // Errors are shown as toasts by the auth hooks, so the modal just stays open on failure.
  const handleConfirmResetPassword = async () => {
    try {
      await resetPassword({ email: user.email });
      setOpenDialog(null);
    } catch {
      // Handled in useForgotPassword.
    }
  };

  const handleConfirmResendActivation = async () => {
    try {
      await resendActivation(user);
      setOpenDialog(null);
    } catch {
      // Handled in useResendActivation.
    }
  };

  const handleConfirmDeactivate = async () => {
    await deactivateUser(user.itemId);
    setOpenDialog(null);
  };

  const handleViewUsage = () => {
    navigate(`/usage?userId=${user.itemId}`, { state: { userName: memberName } });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('TEAMS_MORE_FOR', {
              defaultValue: 'More for {{name}}',
              name: memberName,
            })}
            className={cn(
              'flex size-10 items-center justify-center rounded-card md:size-8',
              triggerClassName
            )}
          >
            <MoreVert width={16} height={16} strokeWidth={1.5} className={mutedColor} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          onCloseAutoFocus={(event) => {
            if (!openDetailsAfterClose.current) return;
            event.preventDefault();
            openDetailsAfterClose.current = false;
            setOpenDialog('details');
          }}
          className={cn(
            'w-48 rounded-section border border-grey-100 bg-true-white p-1.5 shadow-choice-card',
            'dark:border-border dark:bg-card'
          )}
        >
          <DropdownMenuItem
            onSelect={() => {
              openDetailsAfterClose.current = true;
            }}
            className={cn(itemClassName, itemColor)}
          >
            {t('VIEW_DETAILS')}
          </DropdownMenuItem>
          {SHOW_VIEW_USAGE && (
            <DropdownMenuItem onSelect={handleViewUsage} className={cn(itemClassName, itemColor)}>
              {t('VIEW_USAGE')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={() => setOpenDialog('edit')}
            className={cn(itemClassName, itemColor)}
          >
            {t('EDIT_PROFILE')}
          </DropdownMenuItem>
          {user.active ? (
            <DropdownMenuItem
              onSelect={() => setOpenDialog('resetPassword')}
              className={cn(itemClassName, itemColor)}
            >
              {t('RESET_PASSWORD')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onSelect={() => setOpenDialog('resendActivation')}
              className={cn(itemClassName, itemColor)}
            >
              {t('RESEND_ACTIVATION_LINK')}
            </DropdownMenuItem>
          )}
          {user.active && !isCurrentUser && (
            <DropdownMenuItem
              onSelect={() => setOpenDialog('deactivate')}
              className={cn(itemClassName, 'text-error')}
            >
              {t('DEACTIVATE_USER')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {openDialog === 'details' && (
        <UserDetails open onOpenChange={closeDialog} selectedUser={user} />
      )}

      <Dialog open={openDialog === 'edit'} onOpenChange={closeDialog}>
        {openDialog === 'edit' && (
          <EditIamProfileDetails
            userInfo={{
              ...user,
              lastName: user.lastName || '',
              profileImageUrl: user.profileImageUrl || '',
            }}
            onClose={() => setOpenDialog(null)}
          />
        )}
      </Dialog>

      <ConfirmationModal
        open={openDialog === 'resetPassword'}
        onOpenChange={closeDialog}
        title={t('RESET_PASSWORD_FOR_USER')}
        description={t('PASSWORD_RESET_SENT_EMAIL')}
        onConfirm={handleConfirmResetPassword}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />

      <ConfirmationModal
        open={openDialog === 'resendActivation'}
        onOpenChange={closeDialog}
        title={t('ACTIVATE_THIS_USER')}
        description={t('USER_ACTIVATION_RESTORED')}
        onConfirm={handleConfirmResendActivation}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />

      <ConfirmationModal
        open={openDialog === 'deactivate'}
        onOpenChange={closeDialog}
        title={t('DEACTIVATE_USER')}
        description={t('DEACTIVATE_USER_CONFIRMATION')}
        onConfirm={handleConfirmDeactivate}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />
    </>
  );
}
